import { Icon } from '@vallourec-ui/vallourec-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { FlexView } from '../common'
import { MessageWrapper, Label, StyledTextAreaInput } from './style'
import { CustomTextInputProps } from '@/@types/global'

interface InputProps extends CustomTextInputProps {
  label?: string
  inline?: boolean
  width?: string
  margin?: string
  message?: string
  messageType?: 'error' | 'success' | 'warning' | 'info'
  maxChar?: number
  commonMessage?: string
  name: string
  minHeight?: number
}

/** TextAreaInput component
 * Creates a TextArea Input with some custom options
 * @remarks
 * this component is part of the SectionBox component group
 * @param label - The label/title of the input
 * @param name - The name of the input
 * @param required - If the input is required
 * @param placeholder - The placeholder of the input
 * @param inline - If the input should be inline with other elements
 * @param width - The width of the input
 * @param margin - The margin of the input
 * @param message - The message to be displayed under the input
 * @param messageType - The type of message to be displayed
 * @param maxChar - The maximum number of characters allowed it will be shown as span under the text area
 * @param commonMessage - A common message to be displayed under the input
 * @example <TextAreaInput label="Description" name="description" placeholder="Type here..." width="100%" margin="0 0 0 0" />
 * */
const TextAreaInput = React.forwardRef<HTMLInputElement, InputProps>(
  function TextAreaInput(props, ref) {
    const {
      label,
      inline,
      width,
      margin,
      message,
      messageType,
      commonMessage,
      maxChar,
      name,
      minHeight,
      ...rest
    } = props

    const { t } = useTranslation()
    let iconName = ''
    switch (messageType) {
      case 'error':
        iconName = 'cross'
        break
      case 'success':
        iconName = 'check'
        break
      case 'warning':
        iconName = 'warning'
        break
      case 'info':
        iconName = 'info'
        break
    }

    return (
      <FlexView width={width} margin={margin}>
        <FlexView
          backgroundColor="inherit"
          width={width}
          flexDirection={inline ? 'row' : 'column'}
          alignItems={inline ? 'center' : 'flex-start'}
          justifyContent="flex-start"
          fontSize="text-sm"
          fontWeight="medium"
        >
          {label && (
            <Label>
              {rest.required ? `${t<string>(label)}*` : t<string>(label)}
            </Label>
          )}
          <StyledTextAreaInput
            minHeight={minHeight}
            maxLength={maxChar}
            {...rest}
            ref={ref}
          />
        </FlexView>
        {message && (
          <MessageWrapper>
            <Icon
              name="CloseCircleLine"
              color={messageType}
              className="message-icon"
            />
          </MessageWrapper>
        )}
        {maxChar && (
          <MessageWrapper>
            <Icon
              name={iconName}
              color={messageType}
              className="message-icon"
            />
            {t<string>('newAnalysis.characterLimit', { maxChar })}
          </MessageWrapper>
        )}
        {commonMessage && <MessageWrapper>{commonMessage}</MessageWrapper>}
      </FlexView>
    )
  }
)

export default TextAreaInput
